/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
	Box,
	Typography,
	CircularProgress,
	IconButton,
	Button,
	FormGroup,
	FormControlLabel,
	Checkbox,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Map as MapboxMap, NavigationControl, Marker } from 'react-map-gl/mapbox';
import MapViewStyles from './MapViewStyles';
import { MapViewModel } from './MapViewModel';
import { useViewModel } from '../../../../hooks/useViewModel';
import { FormattedMessage } from 'react-intl';

import CancelIcon from '@mui/icons-material/Cancel';
import { Entry, SelectedOptionsMap } from '../../../../types/course/Entry';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import FilterListIcon from '@mui/icons-material/FilterList';
import entryLocationIcon from '../../../../assets/images/entry-location.svg';
import entryPublicIcon from '../../../../assets/images/entry-public.svg';
import entrySharedIcon from '../../../../assets/images/entry-shared.svg';
import { EntryDetailView } from '../EntriesReview/EntryDetail/EntryDetailView';
import 'mapbox-gl/dist/mapbox-gl.css';

type ModuleFilterGroup = {
	id: string;
	name: string;
	modules: ModuleFilterOption[];
};

type ModuleFilterOption = {
	id: string;
	name: string;
	packageId?: string;
	packageName?: string;
	unitNumber?: number;
	questions?: ModuleFilterQuestion[];
};

type ModuleFilterQuestion = {
	id: string;
	key: string;
	title?: string;
	options: string[];
	optionCounts: Record<string, number>;
};

type ModuleOptionStats = Record<
	string,
	Record<
		string,
		{
			title?: string;
			options: string[];
			optionCounts: Record<string, number>;
		}
	>
>;

type QuestionFilters = Record<string, Record<string, string[]>>;

const parseSelectedOptions = (
	selectedOptions: string | SelectedOptionsMap | undefined,
): SelectedOptionsMap | undefined => {
	if (!selectedOptions) {
		return undefined;
	}

	if (typeof selectedOptions === 'string') {
		try {
			return JSON.parse(selectedOptions);
		} catch {
			return undefined;
		}
	}

	return selectedOptions;
};

/**
 * Map view component for displaying a Mapbox map
 * Shows climate adaptation measures and relevant locations in Baden-Württemberg
 *
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
const MapView = observer((): ReactElement => {
	const { classes } = MapViewStyles();
	const viewModel: MapViewModel = useViewModel(MapViewModel);
	const [selectedEntries, setSelectedEntries] = useState<Entry[]>([]);
	const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
	const [selectedTypes, setSelectedTypes] = useState<string[]>(['private', 'shared', 'public']);
	const [selectedModules, setSelectedModules] = useState<string[]>([]);
	const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
	const [selectedQuestionFilters, setSelectedQuestionFilters] = useState<QuestionFilters>({});
	const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
	const [isGroupDialogOpen, setGroupDialogOpen] = useState(false);

	const modulePackages = viewModel.getModulePackages();

	const moduleOptionStats: ModuleOptionStats = useMemo(() => {
		const stats: ModuleOptionStats = {};

		viewModel.approvedEntries.forEach((entry: Entry) => {
			const moduleId = entry.moduleId;
			if (!moduleId) {
				return;
			}

			const selectedOptionsMap = parseSelectedOptions(entry.selectedOptions);

			if (!selectedOptionsMap) {
				return;
			}

			Object.entries(selectedOptionsMap).forEach(([groupKey, optionsData]) => {
				if (!optionsData?.options?.length) {
					return;
				}

				if (!stats[moduleId]) {
					stats[moduleId] = {};
				}

				if (!stats[moduleId][groupKey]) {
					stats[moduleId][groupKey] = {
						title: groupKey,
						options: Array.from(new Set(optionsData.options ?? [])),
						optionCounts: {},
					};
				} else if (optionsData.options?.length) {
					const existingOptions = new Set(stats[moduleId][groupKey].options);
					optionsData.options.forEach((option) => existingOptions.add(option));
					stats[moduleId][groupKey].options = Array.from(existingOptions);
				}

				optionsData.selected?.forEach((selectedOption: string) => {
					if (!selectedOption) {
						return;
					}
					stats[moduleId][groupKey].optionCounts[selectedOption] =
						(stats[moduleId][groupKey].optionCounts[selectedOption] ?? 0) + 1;
				});
			});
		});

		return stats;
	}, [viewModel.approvedEntries]);

	const moduleGroups: ModuleFilterGroup[] = useMemo(() => {
		if (modulePackages.length === 0) {
			return [];
		}

		return modulePackages
			.map((modulePackage) => {
				const modules =
					modulePackage.modules
						?.map((module) => {
							const moduleStats = moduleOptionStats[module.id] ?? {};
							const questionsFromElements: ModuleFilterQuestion[] =
								module.elements
									?.flatMap((element, elementIndex) => {
										const groups = element.optionsGroups ?? [];
										if (groups.length === 0) {
											return [];
										}

										return groups.map((group, groupIndex) => {
											const questionKey = group.title || `${module.id}-${group.id ?? `${elementIndex}-${groupIndex}`}`;
											const stats = moduleStats[questionKey];
											const optionCounts: Record<string, number> = {};

											(group.options ?? []).forEach((option) => {
												optionCounts[option] = stats?.optionCounts?.[option] ?? 0;
											});

											return {
												id: `${module.id}-${group.id ?? `${elementIndex}-${groupIndex}`}`,
												key: questionKey,
												title: group.title,
												options: group.options ?? [],
												optionCounts,
											};
										});
									})
									.filter((question) => question.options.length > 0) ?? [];

							const existingQuestionKeys = new Set(questionsFromElements.map((question) => question.key));
							const statsOnlyQuestions: ModuleFilterQuestion[] = Object.entries(moduleStats)
								.filter(([questionKey]) => !existingQuestionKeys.has(questionKey))
								.map(([questionKey, questionData]) => ({
									id: `${module.id}-${questionKey}`,
									key: questionKey,
									title: questionData.title,
									options: questionData.options,
									optionCounts: questionData.optionCounts,
								}));

							const questions = [...questionsFromElements, ...statsOnlyQuestions];

							return {
								id: module.id || `${modulePackage.id}-${module.name}`,
								name: module.name || module.id || 'Unbenanntes Modul',
								packageId: modulePackage.id,
								packageName: modulePackage.name,
								unitNumber: module.unitNumber ?? 0,
								questions,
							};
						})
						.sort((a, b) => (a.unitNumber ?? 0) - (b.unitNumber ?? 0)) ?? [];

				return {
					id: modulePackage.id,
					name: modulePackage.name,
					modules,
				};
			})
			.filter((group) => group.modules.length > 0);
	}, [modulePackages, moduleOptionStats]);

	const moduleLookup = useMemo(() => {
		const map = new Map<string, ModuleFilterOption>();
		moduleGroups.forEach((group) => {
			group.modules.forEach((module) => {
				map.set(module.id, module);
			});
		});
		return map;
	}, [moduleGroups]);

	const moduleQuestionsLookup = useMemo(() => {
		const map = new Map<string, Map<string, ModuleFilterQuestion>>();
		moduleGroups.forEach((group) => {
			group.modules.forEach((module) => {
				const questionMap = new Map<string, ModuleFilterQuestion>();
				module.questions?.forEach((question) => {
					questionMap.set(question.key, question);
				});
				map.set(module.id, questionMap);
			});
		});
		return map;
	}, [moduleGroups]);

	const hasModuleFilters = selectedModules.length > 0;

	const hasQuestionFilters = useMemo(() => {
		return Object.entries(selectedQuestionFilters).some(([moduleId, moduleFilters]) => {
			const questionMap = moduleQuestionsLookup.get(moduleId);
			return Object.entries(moduleFilters).some(([questionKey, options]) => {
				const totalOptions = questionMap?.get(questionKey)?.options.length ?? 0;
				if (totalOptions === 0) {
					return options.length > 0;
				}
				return options.length < totalOptions;
			});
		});
	}, [selectedQuestionFilters, moduleQuestionsLookup]);

	useEffect(() => {
		setExpandedGroups((prev) => {
			const next: Record<string, boolean> = {};
			moduleGroups.forEach((group) => {
				next[group.id] = prev[group.id] ?? false;
			});
			return next;
		});
	}, [moduleGroups]);

	useEffect(() => {
		setSelectedQuestionFilters((prev) => {
			const moduleQuestionKeys = new Map<string, Set<string>>();
			moduleGroups.forEach((group) => {
				group.modules.forEach((module) => {
					const keys = module.questions?.map((question) => question.key) ?? [];
					moduleQuestionKeys.set(module.id, new Set(keys));
				});
			});

			let changed = false;
			const next: QuestionFilters = {};

			Object.entries(prev).forEach(([moduleId, questionFilters]) => {
				const availableQuestions = moduleQuestionKeys.get(moduleId);
				if (!availableQuestions) {
					changed = true;
					return;
				}

				const filteredQuestions: Record<string, string[]> = {};
				Object.entries(questionFilters).forEach(([questionKey, options]) => {
					const filteredOptions = options.filter(Boolean);
					if (filteredOptions.length === 0 || !availableQuestions.has(questionKey)) {
						changed = true;
						return;
					}
					filteredQuestions[questionKey] = filteredOptions;
				});

				if (Object.keys(filteredQuestions).length > 0) {
					next[moduleId] = filteredQuestions;
				} else if (Object.keys(questionFilters).length > 0) {
					changed = true;
				}
			});

			return changed ? next : prev;
		});
	}, [moduleGroups]);

	const moduleOptions: ModuleFilterOption[] = useMemo(() => {
		if (moduleGroups.length > 0) {
			return moduleGroups.flatMap((group) =>
				group.modules.map((module) => ({
					id: module.id,
					name: module.name,
					packageId: group.id,
					packageName: group.name,
				})),
			);
		}

		const fallback = new Map<string, ModuleFilterOption>();
		viewModel.approvedEntries.forEach((entry) => {
			const moduleId = entry.moduleId ?? entry.module ?? '';
			const moduleName = entry.module ?? moduleId;
			if (moduleId && !fallback.has(moduleId)) {
				fallback.set(moduleId, { id: moduleId, name: moduleName });
			}
		});
		return Array.from(fallback.values());
	}, [moduleGroups, viewModel.approvedEntries]);

	useEffect(() => {
		if (moduleOptions.length === 0) {
			setSelectedModules([]);
			return;
		}

		setSelectedModules((prev) => prev.filter((id) => moduleOptions.some((module) => module.id === id)));
	}, [moduleOptions]);

	const filteredEntries = useMemo(() => {
		if (selectedTypes.length === 0) {
			return [];
		}

		return viewModel.approvedEntries.filter((entry) => {
			const typeMatch = selectedTypes.includes((entry.type ?? 'private').toLowerCase());
			const moduleId = entry.moduleId ?? entry.module ?? '';
			const questionMap = moduleQuestionsLookup.get(moduleId);

			const moduleFilters = selectedQuestionFilters[moduleId];
			const moduleHasQuestionFilter =
				moduleFilters && Object.values(moduleFilters).some((options) => options.length > 0);

			const moduleMatch = (() => {
				if (hasQuestionFilters) {
					if (moduleHasQuestionFilter) {
						return true;
					}
					return hasModuleFilters && moduleId ? selectedModules.includes(moduleId) : false;
				}

				if (!hasModuleFilters) {
					return true;
				}

				return moduleId ? selectedModules.includes(moduleId) : false;
			})();

			if (!moduleMatch || !typeMatch) {
				return false;
			}

			if (!moduleHasQuestionFilter) {
				return true;
			}

			const selectedOptionsMap = parseSelectedOptions(entry.selectedOptions);
			if (!selectedOptionsMap) {
				return false;
			}

			return Object.entries(moduleFilters ?? {}).every(([questionKey, selectedOptions]) => {
				if (selectedOptions.length === 0) {
					return true;
				}

				const questionDefinition = questionMap?.get(questionKey);
				const totalOptions = questionDefinition?.options.length ?? 0;
				if (totalOptions > 0 && selectedOptions.length >= totalOptions) {
					return true;
				}

				const questionData = selectedOptionsMap[questionKey];
				if (!questionData?.selected?.length) {
					return false;
				}

				return selectedOptions.some((option) => questionData.selected?.includes(option));
			});
		});
	}, [
		selectedTypes,
		selectedModules,
		selectedQuestionFilters,
		viewModel.approvedEntries,
		hasModuleFilters,
		hasQuestionFilters,
		moduleQuestionsLookup,
	]);

	const groupedEntries = useMemo(() => viewModel.groupEntries(filteredEntries), [filteredEntries, viewModel]);
	useEffect(() => {
		viewModel.resetViewToGermany();
	}, [viewModel]);

	const entryDetail = viewModel.getEntry();

	function getEntryIcon(entry: Entry): string {
		const type = entry.type?.toLowerCase();
		switch (type) {
			case 'public':
				return entryPublicIcon;
			case 'shared':
				return entrySharedIcon;
			default:
				return entryLocationIcon;
		}
	}

	function getMarkerIcon(group: Entry[]): string {
		if (group.length > 1) {
			return entrySharedIcon;
		}
		return getEntryIcon(group[0]);
	}

	function handleEntryClick(entryId: string): void {
		viewModel.openEntryDetail(entryId);
	}

	function handleLocateMe(): void {
		if (!navigator.geolocation) {
			viewModel.showMapError('map.gps_unsupported');
			return;
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const { latitude, longitude } = position.coords;
				setUserLocation({ latitude, longitude });
				viewModel.centerOnCoordinates(latitude, longitude);
			},
			() => {
				viewModel.showMapError('map.gps_error');
			},
			{ enableHighAccuracy: true, timeout: 10000 },
		);
	}

	function toggleType(type: string): void {
		setSelectedTypes((prev) => {
			if (prev.includes(type)) {
				return prev.filter((item) => item !== type);
			}
			return [...prev, type];
		});
	}

	/**
	 * Removes all stored option selections for the provided module.
	 *
	 * @param moduleId Identifier of the module to clear filters for.
	 */
	function clearModuleQuestionFilters(moduleId: string): void {
		setSelectedQuestionFilters((prev) => {
			if (!prev[moduleId]) {
				return prev;
			}
			const next = { ...prev };
			delete next[moduleId];
			return next;
		});
	}

	/**
	 * Keeps question option filters aligned with the module checkbox state.
	 *
	 * @param moduleId Identifier of the module that changed.
	 * @param shouldSelectAllOptions Whether all available options should become selected.
	 */
	function applyModuleOptionSelection(moduleId: string, shouldSelectAllOptions: boolean): void {
		const moduleDefinition = moduleLookup.get(moduleId);

		if (shouldSelectAllOptions) {
			if (!moduleDefinition?.questions?.length) {
				clearModuleQuestionFilters(moduleId);
				return;
			}

			const moduleFilters = moduleDefinition.questions.reduce<Record<string, string[]>>((acc, question) => {
				if (question.options.length > 0) {
					acc[question.key] = [...question.options];
				}
				return acc;
			}, {});

			if (Object.keys(moduleFilters).length === 0) {
				clearModuleQuestionFilters(moduleId);
				return;
			}

			setSelectedQuestionFilters((prev) => ({
				...prev,
				[moduleId]: moduleFilters,
			}));
			return;
		}

		clearModuleQuestionFilters(moduleId);
	}

	function moduleHasSelectableOptions(moduleId: string): boolean {
		const questionMap = moduleQuestionsLookup.get(moduleId);
		if (!questionMap) {
			return false;
		}

		return Array.from(questionMap.values()).some((question) => question.options.length > 0);
	}

	function isModuleChecked(moduleId: string): boolean {
		const questionMap = moduleQuestionsLookup.get(moduleId);

		if (!moduleHasSelectableOptions(moduleId) || !questionMap || questionMap.size === 0) {
			return selectedModules.includes(moduleId);
		}

		let hasRelevantQuestions = false;

		for (const question of Array.from(questionMap.values())) {
			if (question.options.length === 0) {
				continue;
			}
			hasRelevantQuestions = true;
			const selected = selectedQuestionFilters[moduleId]?.[question.key] ?? [];
			if (selected.length < question.options.length) {
				return false;
			}
		}

		return hasRelevantQuestions;
	}

	function isModuleIndeterminate(moduleId: string): boolean {
		const questionMap = moduleQuestionsLookup.get(moduleId);

		if (!moduleHasSelectableOptions(moduleId) || !questionMap || questionMap.size === 0) {
			return false;
		}

		let hasSelection = false;
		let allSelected = true;

		for (const question of Array.from(questionMap.values())) {
			if (question.options.length === 0) {
				continue;
			}
			const selectedCount = selectedQuestionFilters[moduleId]?.[question.key]?.length ?? 0;
			if (selectedCount > 0) {
				hasSelection = true;
			}
			if (selectedCount < question.options.length) {
				allSelected = false;
			}
		}

		return hasSelection && !allSelected;
	}

	function toggleModule(moduleId: string): void {
		const isSelected = selectedModules.includes(moduleId);
		setSelectedModules((prev) => {
			if (isSelected) {
				return prev.filter((item) => item !== moduleId);
			}
			return [...prev, moduleId];
		});
		applyModuleOptionSelection(moduleId, !isSelected);
	}

	function toggleQuestionOption(moduleId: string, questionKey: string, option: string): void {
		setSelectedQuestionFilters((prev) => {
			const moduleFilters = { ...(prev[moduleId] ?? {}) };
			const currentOptions = moduleFilters[questionKey] ? [...moduleFilters[questionKey]] : [];
			const optionIndex = currentOptions.indexOf(option);

			if (optionIndex >= 0) {
				currentOptions.splice(optionIndex, 1);
			} else {
				currentOptions.push(option);
			}

			if (currentOptions.length > 0) {
				moduleFilters[questionKey] = currentOptions;
			} else {
				delete moduleFilters[questionKey];
			}

			const next = { ...prev };
			if (Object.keys(moduleFilters).length > 0) {
				next[moduleId] = moduleFilters;
			} else {
				delete next[moduleId];
			}

			return next;
		});
	}

	function isQuestionOptionSelected(moduleId: string, questionKey: string, option: string): boolean {
		return selectedQuestionFilters[moduleId]?.[questionKey]?.includes(option) ?? false;
	}

	function toggleGroupExpanded(groupId: string): void {
		setExpandedGroups((prev) => ({
			...prev,
			[groupId]: !prev[groupId],
		}));
	}

	const isGroupExpanded = (groupId: string): boolean => expandedGroups[groupId] ?? false;

	function toggleModuleGroup(group: ModuleFilterGroup): void {
		const groupModuleIds = group.modules.map((module) => module.id);
		const allSelected = groupModuleIds.every((id) => selectedModules.includes(id));

		if (allSelected) {
			setSelectedModules((prev) => prev.filter((id) => !groupModuleIds.includes(id)));
			groupModuleIds.forEach((moduleId) => applyModuleOptionSelection(moduleId, false));
			return;
		}

		const newlySelected = groupModuleIds.filter((id) => !selectedModules.includes(id));
		setSelectedModules((prev) => {
			const uniqueSelection = new Set(prev);
			groupModuleIds.forEach((id) => uniqueSelection.add(id));
			return Array.from(uniqueSelection);
		});
		newlySelected.forEach((moduleId) => applyModuleOptionSelection(moduleId, true));
	}

	const isGroupFullySelected = (group: ModuleFilterGroup): boolean =>
		group.modules.every((module) => selectedModules.includes(module.id));

	const isGroupPartiallySelected = (group: ModuleFilterGroup): boolean => {
		const hasSome = group.modules.some((module) => selectedModules.includes(module.id));
		return hasSome && !isGroupFullySelected(group);
	};

	// Check if Mapbox token is configured
	if (!viewModel.hasMapboxToken()) {
		return (
			<Box className={classes.container}>
				<Box className={classes.errorContainer}>
					<Typography variant='h6' className={classes.errorText}>
						<FormattedMessage id='map.error.missing_token' />
					</Typography>
				</Box>
			</Box>
		);
	}

	// Show loading state while entries are being fetched
	if (viewModel.isLoadingEntries) {
		return (
			<Box className={classes.container}>
				<Box className={classes.loadingContainer}>
					<CircularProgress />
				</Box>
			</Box>
		);
	}

	return (
		<Box className={classes.container}>
			<Box className={classes.actionButtons}>
				<Button
					variant='contained'
					size='small'
					color='secondary'
					startIcon={<MyLocationIcon />}
					onClick={handleLocateMe}
					className={classes.actionButton}>
					<FormattedMessage id='map.locate_me' />
				</Button>
				<Button
					variant='contained'
					size='small'
					color='secondary'
					startIcon={<FilterListIcon />}
					onClick={() => setIsFilterDialogOpen(true)}
					className={classes.actionButton}>
					<FormattedMessage id='map.filter' />
				</Button>
				<Dialog
					open={isFilterDialogOpen}
					onClose={() => setIsFilterDialogOpen(false)}
					fullWidth
					maxWidth='sm'
					aria-labelledby='map-filter-dialog-title'>
					<DialogTitle id='map-filter-dialog-title' className={classes.filterDialogTitle}>
						<Box display='flex' alignItems='center' justifyContent='space-between'>
							<Typography variant='h6'>
								<FormattedMessage id='map.filter' />
							</Typography>
							<IconButton onClick={() => setIsFilterDialogOpen(false)}>
								<CancelIcon fontSize='small' />
							</IconButton>
						</Box>
					</DialogTitle>
					<DialogContent dividers className={classes.filterDialogContent}>
						<Box className={classes.filterMenu}>
							<Typography variant='subtitle2' className={classes.filterTitle}>
								<FormattedMessage id='map.filter.types' />
							</Typography>
							<FormGroup className={classes.filterGroup}>
								{['public', 'shared', 'private'].map((type) => (
									<FormControlLabel
										key={type}
										control={
											<Checkbox checked={selectedTypes.includes(type)} onChange={() => toggleType(type)} size='small' />
										}
										style={{ marginRight: 0 }}
										label={<FormattedMessage id={`entries.type.${type}`} />}
									/>
								))}
							</FormGroup>
							<Typography variant='subtitle2' className={classes.filterSectionTitle}>
								<FormattedMessage id='map.filter.modules' />
							</Typography>
							{moduleGroups.length === 0 ? (
								<FormGroup className={classes.filterGroup}>
									{moduleOptions.length === 0 && (
										<Typography variant='body2' className={classes.filterEmpty}>
											<FormattedMessage id='map.filter.modules_empty' />
										</Typography>
									)}
									{moduleOptions.map((module) => (
										<FormControlLabel
											key={module.id}
											className={classes.filterModuleItem}
											control={
												<Checkbox
													checked={selectedModules.includes(module.id)}
													onChange={() => toggleModule(module.id)}
													size='small'
												/>
											}
											label={module.name}
										/>
									))}
								</FormGroup>
							) : (
								<Box>
									{moduleGroups.map((group) => (
										<Box key={group.id} className={classes.filterPackage}>
											<Box className={classes.filterPackageHeader}>
												<FormControlLabel
													className={classes.filterPackageCheckbox}
													control={
														<Checkbox
															checked={isGroupFullySelected(group)}
															indeterminate={isGroupPartiallySelected(group)}
															onChange={() => toggleModuleGroup(group)}
															size='small'
														/>
													}
													label={
														<Typography variant='body2' className={classes.filterPackageTitle}>
															{group.name}
														</Typography>
													}
												/>
												<IconButton
													size='small'
													className={classes.filterPackageToggle}
													aria-label='toggle-module-group'
													onClick={() => toggleGroupExpanded(group.id)}>
													<ExpandMoreIcon
														className={isGroupExpanded(group.id) ? classes.expandIconOpen : classes.expandIcon}
													/>
												</IconButton>
											</Box>
											<Collapse in={isGroupExpanded(group.id)}>
												<FormGroup className={classes.filterGroup}>
													{group.modules.map((module) => (
														<Box key={module.id} className={classes.filterModuleWrapper}>
															<FormControlLabel
																className={classes.filterModuleItem}
																style={{ marginRight: 0 }}
																control={
																	<Checkbox
																		checked={isModuleChecked(module.id)}
																		indeterminate={isModuleIndeterminate(module.id)}
																		onChange={() => toggleModule(module.id)}
																		size='small'
																	/>
																}
																label={module.name}
															/>
															{module.questions && module.questions.length > 0 && (
																<Box className={classes.filterQuestionList}>
																	{module.questions.map((question) => (
																		<Box key={question.id} className={classes.filterQuestionItem}>
																			{question.title && (
																				<Typography variant='caption' className={classes.filterQuestionTitle}>
																					{question.title}
																				</Typography>
																			)}
																			<Box className={classes.filterQuestionOptions}>
																				{question.options.map((option, index) => (
																					<Box
																						key={`${question.id}-${index}`}
																						className={classes.filterQuestionOptionRow}>
																						<Checkbox
																							size='small'
																							checked={isQuestionOptionSelected(module.id, question.key, option)}
																							onChange={() => toggleQuestionOption(module.id, question.key, option)}
																						/>
																						<Typography variant='caption' className={classes.filterQuestionOption}>
																							<span>{option}</span>
																						</Typography>
																					</Box>
																				))}
																			</Box>
																		</Box>
																	))}
																</Box>
															)}
														</Box>
													))}
												</FormGroup>
											</Collapse>
										</Box>
									))}
								</Box>
							)}
						</Box>
					</DialogContent>
					<DialogActions className={classes.filterDialogActions}>
						<Button onClick={() => setIsFilterDialogOpen(false)} color='primary'>
							<FormattedMessage id='general.cancel' defaultMessage='Abbrechen' />
						</Button>
						<Button onClick={() => setIsFilterDialogOpen(false)} variant='contained' color='primary'>
							<FormattedMessage id='map.filter.apply' defaultMessage='Anwenden' />
						</Button>
					</DialogActions>
				</Dialog>
			</Box>

			<Box className={classes.mapContainer}>
				<MapboxMap
					{...viewModel.viewState}
					onMove={(evt) => viewModel.updateViewState(evt.viewState)}
					mapStyle={viewModel.getMapStyle()}
					mapboxAccessToken={viewModel.getMapboxToken()}
					style={{ width: '100%', height: '100%' }}>
					<NavigationControl position='top-right' />

					{userLocation && (
						<Marker longitude={userLocation.longitude} latitude={userLocation.latitude} anchor='center'>
							<Box className={classes.userLocationMarker}>
								<Box className={classes.userLocationPulse} />
								<Box className={classes.userLocationDot} />
							</Box>
						</Marker>
					)}

					{/* Render markers for approved entries */}
					{groupedEntries.map((group, index) => {
						const isCluster = group.length > 1;
						const iconSrc = getMarkerIcon(group);

						return (
							<Marker
								key={`${group[0].id}-${index}`}
								longitude={group[0].long}
								latitude={group[0].lat}
								anchor='bottom'
								onClick={(e) => {
									e.originalEvent.stopPropagation();
									if (group.length === 1) {
										handleEntryClick(group[0].id);
										return;
									}
									setSelectedEntries(group);
									setGroupDialogOpen(true);
								}}>
								<Box className={classes.marker}>
									<img
										src={iconSrc}
										alt='course entry marker'
										className={isCluster ? classes.markerIconCluster : classes.markerIcon}
										draggable={false}
									/>
								</Box>
							</Marker>
						);
					})}
				</MapboxMap>
			</Box>
			<Dialog
				open={isGroupDialogOpen && selectedEntries.length > 1}
				onClose={() => {
					setGroupDialogOpen(false);
					setSelectedEntries([]);
				}}
				fullWidth
				maxWidth='sm'>
				<DialogTitle className={classes.groupDialogTitle}>
					<Box>
						<Typography variant='h6' className={classes.groupDialogHeading}>
							<FormattedMessage id='map.entries_group_title' defaultMessage='Einträge an diesem Ort' />
						</Typography>
						<Typography variant='body2' className={classes.groupDialogSubtitle}>
							<FormattedMessage
								id='map.entries_group_count'
								defaultMessage='{count} Einträge'
								values={{ count: selectedEntries.length }}
							/>
						</Typography>
					</Box>

					<CancelIcon
						fontSize={'small'}
						onClick={() => {
							setGroupDialogOpen(false);
							setSelectedEntries([]);
						}}
						className={classes.closeButton}
					/>
				</DialogTitle>
				<DialogContent className={classes.groupDialogContent}>
					<Box className={classes.groupDialogList}>
						{selectedEntries.map((entry) => (
							<Box
								key={entry.id}
								className={classes.groupDialogItem}
								onClick={() => {
									handleEntryClick(entry.id);
									setGroupDialogOpen(false);
									setSelectedEntries([]);
								}}>
								<Box className={classes.groupDialogImageWrapper}>
									<img
										src={entry.imageUrl || entry.studentImgUrl || 'https://via.placeholder.com/72x72.png?text=Entry'}
										alt={entry.module}
										className={classes.groupDialogImage}
									/>
								</Box>
								<Box className={classes.groupDialogContentInfo}>
									<Typography variant='subtitle1' className={classes.groupDialogItemTitle}>
										{entry.module}
									</Typography>
									<Box className={classes.groupDialogMeta}>
										<Typography variant='caption' className={classes.groupDialogStudent}>
											{entry.studentName}
										</Typography>
										<Typography variant='caption' className={classes.groupDialogDate}>
											{new Date(entry.date).toLocaleDateString('de-DE')}
										</Typography>
									</Box>
								</Box>
							</Box>
						))}
					</Box>
				</DialogContent>
			</Dialog>
			{!!entryDetail && <EntryDetailView open={viewModel.isDetailView()} entry={entryDetail} isMapView />}
		</Box>
	);
});

export default MapView;
